import React, { useEffect, useMemo, useState } from "react";
import { Sparkles, X } from "lucide-react";

const DISMISS_KEY = "bananax_flash_dismissed_v1";

// End-of-month at 23:59:59 (local time) — refreshes naturally each month
function getEndOfMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).getTime();
}

function fmt(ms) {
  if (ms <= 0) return { d: 0, h: 0, m: 0, s: 0, ended: true };
  const s = Math.floor(ms / 1000);
  return {
    d: Math.floor(s / 86400),
    h: Math.floor((s % 86400) / 3600),
    m: Math.floor((s % 3600) / 60),
    s: s % 60,
    ended: false,
  };
}

const Cell = ({ value, label }) => (
  <div className="flex flex-col items-center min-w-[34px]">
    <span className="num-display text-bone text-base sm:text-lg leading-none">
      {String(value).padStart(2, "0")}
    </span>
    <span className="label-eyebrow text-[8px] text-bone/50 mt-0.5">{label}</span>
  </div>
);

export default function FlashSaleBanner({ label = "Refined for the season", offer = "25% off storewide", variant = "full" }) {
  const deadline = useMemo(() => getEndOfMonth(), []);
  const [now, setNow] = useState(Date.now());
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (dismissed) return undefined;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [dismissed]);

  if (dismissed) return null;
  const t = fmt(deadline - now);
  if (t.ended) return null;

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
    setDismissed(true);
  };

  // Slim variant — single-line, no pill copy, used on homepage hero
  if (variant === "slim") {
    return (
      <div
        className="border-b border-gold/20 bg-ink-950/70 backdrop-blur-sm"
        data-testid="flash-sale-banner-slim"
      >
        <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-2 flex items-center justify-center gap-3 text-xs">
          <Sparkles className="w-3 h-3 text-gold shrink-0" />
          <span className="label-eyebrow text-gold">{label} · {offer} · ends in</span>
          <div className="flex items-center gap-1.5 num-display text-bone tracking-wider" data-testid="flash-sale-countdown-slim">
            {t.d > 0 && <><span>{String(t.d).padStart(2, "0")}d</span><span className="text-gold/40">:</span></>}
            <span>{String(t.h).padStart(2, "0")}h</span><span className="text-gold/40">:</span>
            <span>{String(t.m).padStart(2, "0")}m</span><span className="text-gold/40">:</span>
            <span className="text-gold">{String(t.s).padStart(2, "0")}s</span>
          </div>
          <button
            onClick={dismiss}
            aria-label="Dismiss flash sale banner"
            className="ml-2 text-mist/50 hover:text-bone transition-colors shrink-0"
            data-testid="flash-sale-dismiss-slim"
          >
            <X className="w-3 h-3" />
          </button>
        </div>
      </div>
    );
  }

  return (
    <div
      className="relative border-y border-gold/30 overflow-hidden"
      style={{
        background:
          "linear-gradient(90deg, rgba(135,32,46,0.18), rgba(11,10,10,0.6) 50%, rgba(212,175,55,0.18))",
      }}
      data-testid="flash-sale-banner"
    >
      <div className="max-w-[1440px] mx-auto px-6 md:px-10 py-3 flex items-center justify-center gap-3 sm:gap-6 text-sm">
        <Sparkles className="w-3.5 h-3.5 text-gold shrink-0" />
        <p className="text-bone/90 font-light tracking-wide hidden sm:block">
          <span className="label-eyebrow text-gold mr-2">{label}</span>
          <span className="text-bone">{offer}</span>
          <span className="text-mist mx-2 hidden md:inline">·</span>
          <span className="text-mist hidden md:inline">ends in</span>
        </p>
        <p className="text-bone/90 font-light text-xs sm:hidden">
          <span className="label-eyebrow text-gold">{label}</span>
        </p>

        <div className="flex items-center gap-2 sm:gap-3" data-testid="flash-sale-countdown">
          {t.d > 0 && <><Cell value={t.d} label="DAYS" /><span className="text-gold/50">:</span></>}
          <Cell value={t.h} label="HRS" />
          <span className="text-gold/50">:</span>
          <Cell value={t.m} label="MIN" />
          <span className="text-gold/50">:</span>
          <Cell value={t.s} label="SEC" />
        </div>

        <button
          onClick={dismiss}
          aria-label="Dismiss flash sale banner"
          className="ml-1 sm:ml-3 text-mist/60 hover:text-bone transition-colors shrink-0"
          data-testid="flash-sale-dismiss"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}
